import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  IconAlertTriangle,
  IconCopy,
  IconDownload,
  IconHeadset,
  IconLoader2,
  IconReceipt,
} from "@tabler/icons-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils/cn";
import { buttonClass } from "../lib/ui";
import { formatMoney, formatDate } from "../lib/format";
import { deriveStatus } from "../api";
import type { MyPayment, PaymentStatusFilter } from "../api";
import { useMyPayments } from "../hooks/useMyPayments";
import { PaymentMethodLabel } from "./PaymentMethodLabel";
import { PaymentStatusBadge } from "./PaymentStatusBadge";

const FILTERS: readonly PaymentStatusFilter[] = [
  "all",
  "scheduled",
  "overdue",
  "paid",
  "refunded",
] as const;

const PAGE_SIZE = 10;

interface Props {
  pastDue: boolean;
}

export function PaymentHistoryList({ pastDue }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: "subscription" });
  const [filter, setFilter] = useState<PaymentStatusFilter>(pastDue ? "overdue" : "all");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useMyPayments({ status: filter, page, pageSize: PAGE_SIZE });

  function changeFilter(next: PaymentStatusFilter) {
    setFilter(next);
    setPage(1);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("history.title")}</CardTitle>
        <p className="text-[12.5px] text-ink-muted">{t("history.subtitle")}</p>
        <div className="mt-3 flex flex-wrap gap-1.5" role="tablist" aria-label={t("history.filterLabel")}>
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              className={buttonClass(f === filter ? "primary" : "secondary")}
              onClick={() => changeFilter(f)}
              role="tab"
              aria-selected={f === filter}
            >
              {t(`history.filter.${f}`)}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading || !data ? (
          <div className="flex items-center justify-center py-12 text-ink-soft">
            <IconLoader2 className="animate-spin" size={20} aria-hidden />
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          <ul className="divide-y divide-border-soft">
            {data.items.map((p) => (
              <PaymentRow key={p.id} payment={p} />
            ))}
          </ul>
        )}

        {data && data.total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3">
            <p className="text-[12px] text-ink-muted">
              {t("history.pagination.summary", {
                from: (data.page - 1) * data.pageSize + 1,
                to: Math.min(data.total, data.page * data.pageSize),
                total: data.total,
              })}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className={buttonClass("secondary")}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={data.page <= 1}
              >
                {t("history.pagination.prev")}
              </button>
              <button
                type="button"
                className={buttonClass("secondary")}
                onClick={() => setPage((p) => p + 1)}
                disabled={data.page * data.pageSize >= data.total}
              >
                {t("history.pagination.next")}
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PaymentRow({ payment }: { payment: MyPayment }) {
  const { t } = useTranslation(undefined, { keyPrefix: "subscription" });
  const derived = deriveStatus(payment);
  const isOverdue = derived === "overdue";

  return (
    <li
      className={cn(
        "flex flex-wrap items-center gap-x-4 gap-y-2 p-4",
        isOverdue && "border-l-4 border-danger bg-danger-soft/20",
      )}
    >
      <div className="min-w-0 flex-1 basis-[180px]">
        <p className="text-[13px] font-semibold text-ink">
          {t("history.competence")} {formatCompetence(payment.competence)}
        </p>
        <p className="text-[12px] text-ink-muted">
          <PaymentMethodLabel method={payment.method} />
        </p>
      </div>

      <div className="min-w-0 basis-[100px]">
        <p className="text-[11.5px] uppercase tracking-wide text-ink-soft">{t("history.amount")}</p>
        <p className="text-[13.5px] font-semibold text-ink">{formatMoney(payment.amountCents / 100)}</p>
      </div>

      <div className="min-w-0 basis-[110px]">
        <p className="text-[11.5px] uppercase tracking-wide text-ink-soft">{t("history.dueDate")}</p>
        <p className="text-[13px] text-ink">{formatDate(payment.dueDate)}</p>
      </div>

      <div className="min-w-0 basis-[110px]">
        <p className="text-[11.5px] uppercase tracking-wide text-ink-soft">{t("history.paidAt")}</p>
        <p className="text-[13px] text-ink">{payment.paidAt ? formatDate(payment.paidAt) : "—"}</p>
      </div>

      <div className="basis-[110px]">
        <PaymentStatusBadge status={derived} />
        {isOverdue && (
          <p className="mt-1 flex items-center gap-1 text-[11px] text-danger-ink">
            <IconAlertTriangle size={11} aria-hidden />
            {t("history.overdueHint")}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        <PaymentActions payment={payment} derived={derived} />
      </div>
    </li>
  );
}

function PaymentActions({
  payment,
  derived,
}: {
  payment: MyPayment;
  derived: ReturnType<typeof deriveStatus>;
}) {
  const { t } = useTranslation(undefined, { keyPrefix: "subscription" });

  async function copy(text: string, key: "barcodeCopied" | "pixCopied") {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t(`actions.${key}`));
    } catch {
      toast.error(t("actions.copyError"));
    }
  }

  if (derived === "paid" && payment.receiptUrl) {
    return (
      <a className={buttonClass("ghost")} href={payment.receiptUrl} target="_blank" rel="noreferrer">
        <IconReceipt size={14} />
        {t("actions.receipt")}
      </a>
    );
  }

  if (derived === "failed" || derived === "chargeback") {
    return (
      <a className={buttonClass("ghost")} href="mailto:suporte@imobgo.com">
        <IconHeadset size={14} />
        {t("actions.support")}
      </a>
    );
  }

  if (payment.method === "boleto" && payment.boleto) {
    return (
      <>
        <button
          type="button"
          className={buttonClass("ghost")}
          onClick={() => copy(payment.boleto!.barcode, "barcodeCopied")}
        >
          <IconCopy size={14} />
          {t("actions.copyBarcode")}
        </button>
        <a className={buttonClass("ghost")} href={payment.boleto.pdfUrl} target="_blank" rel="noreferrer">
          <IconDownload size={14} />
          {t("actions.pdf")}
        </a>
      </>
    );
  }

  if (payment.method === "pix" && payment.pix) {
    return (
      <button
        type="button"
        className={buttonClass("ghost")}
        onClick={() => copy(payment.pix!.copyPaste, "pixCopied")}
      >
        <IconCopy size={14} />
        {t("actions.copyPix")}
      </button>
    );
  }

  return null;
}

function EmptyState({ filter }: { filter: PaymentStatusFilter }) {
  const { t } = useTranslation(undefined, { keyPrefix: "subscription" });
  return (
    <div className="px-6 py-10 text-center">
      <p className="text-[13.5px] font-medium text-ink">{t(`history.empty.${filter}.title`)}</p>
      <p className="mt-1 text-[12.5px] text-ink-muted">{t(`history.empty.${filter}.subtitle`)}</p>
    </div>
  );
}

function formatCompetence(competence: string): string {
  const [year, month] = competence.split("-");
  return `${month}/${year}`;
}
